import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { imgUrl, inr, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";

const EMPTY_PLAN = {
  title: "", category: "Movies", description: "", min_investment: "", expected_return: "",
  duration_months: "", duration_label: "", risk: "Low", image: "", features: "", rate_table: [], offer_start: "", offer_end: "", return_percent: "", duration_days: "", credit_schedule: "maturity", active: true,
};
const EMPTY_ROW = { label: "", amount: "", interest: "", total: "" };

const RateTableEditor = ({ rows, onChange }) => {
  const cell = "bg-transparent border border-white/10 px-3 py-2 text-xs text-white placeholder:text-white/25 gold-focus w-full";
  const update = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-3" data-testid="rate-table-editor">
      <div className="flex items-center justify-between">
        <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-white/50">Rate Chart (optional)</p>
        <button type="button" data-testid="rate-row-add-button" onClick={() => onChange([...rows, EMPTY_ROW])} className="font-accent text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] hover:text-white transition-colors">
          + Add Row
        </button>
      </div>
      {rows.length > 0 && (
        <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">
          <span>Label</span><span>Amount ₹</span><span>Interest ₹</span><span>Total ₹</span><span className="w-6" />
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`rate-row-editor-${i}`}>
          <input data-testid={`rate-row-label-${i}`} placeholder="e.g. ₹50 / day" value={r.label} onChange={(e) => update(i, "label", e.target.value)} className={cell} />
          <input data-testid={`rate-row-amount-${i}`} type="number" required placeholder="10000" value={r.amount} onChange={(e) => update(i, "amount", e.target.value)} className={cell} />
          <input data-testid={`rate-row-interest-${i}`} type="number" required placeholder="3000" value={r.interest} onChange={(e) => update(i, "interest", e.target.value)} className={cell} />
          <input data-testid={`rate-row-total-${i}`} type="number" required placeholder="13000" value={r.total} onChange={(e) => update(i, "total", e.target.value)} className={cell} />
          <button type="button" data-testid={`rate-row-remove-${i}`} onClick={() => onChange(rows.filter((_, idx) => idx !== i))} className="w-6 text-white/40 hover:text-red-400 text-lg leading-none">×</button>
        </div>
      ))}
    </div>
  );
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const PlanForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    ...initial,
    rate_table: initial.rate_table || [],
    duration_label: initial.duration_label || "",
    offer_start: toLocalInput(initial.offer_start),
    offer_end: toLocalInput(initial.offer_end),
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image: data.path }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      category: form.category,
      description: form.description,
      min_investment: Number(form.min_investment),
      expected_return: form.expected_return,
      duration_months: Number(form.duration_months),
      duration_label: form.duration_label,
      risk: form.risk,
      image: form.image,
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
      rate_table: form.rate_table.map((r) => ({ label: r.label || "", amount: Number(r.amount), interest: Number(r.interest), total: Number(r.total) })),
      offer_start: form.offer_start ? new Date(form.offer_start).toISOString() : "",
      offer_end: form.offer_end ? new Date(form.offer_end).toISOString() : "",
      return_percent: Number(form.return_percent) || 0,
      duration_days: Number(form.duration_days) || 0,
      credit_schedule: form.credit_schedule || "maturity",
      active: Boolean(form.active),
    };
    try {
      if (form.id) await api.put(`/admin/plans/${form.id}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success(form.id ? "Plan updated" : "Plan created");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#0A0A0A] border border-[#D4AF37]/30 p-8 space-y-5" data-testid="plan-form">
      <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">{form.id ? "Edit Plan" : "New Plan"}</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <input data-testid="plan-title-input" required placeholder="Plan title" value={form.title} onChange={set("title")} className={inputCls} />
        <select data-testid="plan-category-select" value={form.category} onChange={set("category")} className={`${inputCls} bg-[#0A0A0A]`}>
          <option>Movies</option>
          <option>Gold</option>
          <option>Forex</option>
          <option>Fixed Deposit</option>
          <option>Daily Investment</option>
          <option>Daily Returns</option>
          <option>Other</option>
        </select>
        <input data-testid="plan-min-investment-input" required type="number" min="1" placeholder="Minimum investment (₹)" value={form.min_investment} onChange={set("min_investment")} className={inputCls} />
        <input data-testid="plan-expected-return-input" required placeholder="Expected return (e.g. 18–24% p.a.)" value={form.expected_return} onChange={set("expected_return")} className={inputCls} />
        <input data-testid="plan-duration-input" required type="number" min="1" placeholder="Duration (months)" value={form.duration_months} onChange={set("duration_months")} className={inputCls} />
        <input data-testid="plan-duration-label-input" placeholder="Duration label shown on site (e.g. 15 Days) — optional" value={form.duration_label} onChange={set("duration_label")} className={inputCls} />
        <input data-testid="plan-return-percent-input" required type="number" min="0" step="0.01" placeholder="Total return % over the term (e.g. 30) — used for maturity tracker" value={form.return_percent ?? ""} onChange={set("return_percent")} className={inputCls} />
        <input data-testid="plan-duration-days-input" required type="number" min="1" placeholder="Term in days (e.g. 15) — used for maturity date" value={form.duration_days ?? ""} onChange={set("duration_days")} className={inputCls} />
        <select data-testid="plan-credit-schedule-select" value={form.credit_schedule || "maturity"} onChange={set("credit_schedule")} className={`${inputCls} bg-[#0A0A0A]`}>
          <option value="maturity">Interest paid at maturity</option>
          <option value="daily">Interest credited daily</option>
          <option value="every_15_days">Interest credited every 15 days</option>
        </select>
        <select data-testid="plan-risk-select" value={form.risk} onChange={set("risk")} className={`${inputCls} bg-[#0A0A0A]`}>
          <option>Low</option>
          <option>Moderate</option>
          <option>High</option>
        </select>
      </div>
      <textarea data-testid="plan-description-input" required rows={3} placeholder="Plan description" value={form.description} onChange={set("description")} className={inputCls} />
      <textarea data-testid="plan-features-input" rows={3} placeholder={"Features — one per line"} value={form.features} onChange={set("features")} className={inputCls} />
      <RateTableEditor rows={form.rate_table} onChange={(rate_table) => setForm({ ...form, rate_table })} />
      <div className="space-y-3" data-testid="offer-window-editor">
        <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-white/50">Limited Offer Window (optional) — shows a live countdown on the card</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <label className="text-xs text-white/40 space-y-1">
            <span>Offer starts</span>
            <input data-testid="plan-offer-start-input" type="datetime-local" value={form.offer_start} onChange={set("offer_start")} className={`${inputCls} [color-scheme:dark]`} />
          </label>
          <label className="text-xs text-white/40 space-y-1">
            <span>Offer ends</span>
            <input data-testid="plan-offer-end-input" type="datetime-local" value={form.offer_end} onChange={set("offer_end")} className={`${inputCls} [color-scheme:dark]`} />
          </label>
        </div>
      </div>
      <div className="flex items-center gap-6 flex-wrap">
        <label className="border border-white/20 px-5 py-3 font-accent text-[10px] tracking-[0.2em] uppercase text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors cursor-pointer" data-testid="plan-image-upload-label">
          {uploading ? "Uploading…" : "Upload Plan Image"}
          <input data-testid="plan-image-upload-input" type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} />
        </label>
        {form.image && <img src={imgUrl(form.image)} alt="Plan" className="w-16 h-20 object-cover border border-white/10" data-testid="plan-image-preview" />}
        <label className="flex items-center gap-3 text-sm text-white/60">
          <input data-testid="plan-active-checkbox" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#D4AF37]" />
          Visible on site
        </label>
      </div>
      <div className="flex gap-4">
        <button data-testid="plan-save-button" disabled={saving || uploading} className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">
          {saving ? "Saving…" : "Save Plan"}
        </button>
        <button type="button" data-testid="plan-cancel-button" onClick={onCancel} className="border border-white/20 text-white/60 px-8 py-3 font-accent text-[11px] tracking-[0.25em] uppercase hover:border-white/50 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

export const PlansTab = () => {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/plans").then((r) => setPlans(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this plan permanently?")) return;
    await api.delete(`/admin/plans/${id}`);
    toast.success("Plan deleted");
    load();
  };

  return (
    <div data-testid="admin-plans-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-display text-2xl tracking-tight">Investment Plans</h2>
        {!editing && (
          <button data-testid="new-plan-button" onClick={() => setEditing(EMPTY_PLAN)} className="border border-[#D4AF37] text-[#D4AF37] px-6 py-3 font-accent text-[10px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors">
            + New Plan
          </button>
        )}
      </div>
      {editing ? (
        <PlanForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
      ) : (
        <div className="border border-white/10">
          {plans.map((p) => (
            <div key={p.id} className="flex items-center gap-6 px-6 py-5 border-b border-white/10 last:border-0" data-testid={`admin-plan-row-${p.id}`}>
              <img src={imgUrl(p.image)} alt={p.title} className="w-12 h-16 object-cover" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{p.title}</p>
                <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35 mt-1">
                  {p.category} · min {inr(p.min_investment)} · {p.expected_return} · {p.rate_table?.length ? `${p.rate_table.length} rate rows · ` : ""}{p.active ? "Live" : "Hidden"}
                </p>
              </div>
              <button data-testid={`edit-plan-${p.id}`} onClick={() => setEditing({ ...p, features: (p.features || []).join("\n") })} className="font-accent text-[10px] tracking-[0.2em] uppercase text-white/60 hover:text-[#D4AF37] transition-colors">
                Edit
              </button>
              <button data-testid={`delete-plan-${p.id}`} onClick={() => remove(p.id)} className="font-accent text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-red-400 transition-colors">
                Delete
              </button>
            </div>
          ))}
          {plans.length === 0 && <p className="px-6 py-8 text-sm text-white/40">No plans yet.</p>}
        </div>
      )}
    </div>
  );
};
